import React, { useState, useEffect, useCallback } from 'react';
import BoxForm from './components/BoxForm';
import BoxKarte from './components/BoxKarte';
import LoginPage from './components/LoginPage';
import LabelPrint from './components/LabelPrint';
import LocationPrint from './components/LocationPrint';
import LocationAssign from './components/LocationAssign';
import { fetchBoxen, fetchKategorien, logout } from './api';
import {
  clearPendingLocation,
  loadPendingLocation,
  locationBaseFromPath,
  savePendingLocation,
  suggestLevel,
} from './locationFlow';

function isPreparedBox(box) {
  return !box.parent_box && !box.kategorie && !box.inhalt_stichworte &&
    !box.standort && !box.erinnerung_am && !box.erinnerung_text &&
    (!box.fotos || box.fotos.length === 0);
}

function localDateKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function reminderDateKey(value) {
  return String(value || '').slice(0, 10);
}

function formatReminderDate(value) {
  const dateKey = reminderDateKey(value);
  return dateKey
    ? new Intl.DateTimeFormat('de-DE').format(new Date(`${dateKey}T12:00:00`))
    : '';
}

function boxNumberFromPath() {
  const match = window.location.pathname.match(/^\/box\/([^/]+)\/?$/i);
  return match ? decodeURIComponent(match[1]).toUpperCase() : '';
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(null); // null = noch prüfen
  const [boxen, setBoxen] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showLabelPrint, setShowLabelPrint] = useState(false);
  const [showLocationPrint, setShowLocationPrint] = useState(false);
  const [locationAssignment, setLocationAssignment] = useState(null);
  const [showPrepared, setShowPrepared] = useState(false);
  const [editBox, setEditBox] = useState(null);
  const [newBoxNumber, setNewBoxNumber] = useState('');
  const [filterKat, setFilterKat] = useState('');
  const [search, setSearch] = useState('');
  const [directLinkHandled, setDirectLinkHandled] = useState(false);
  const [directLocationHandled, setDirectLocationHandled] = useState(false);
  const requestedBoxNumber = boxNumberFromPath();
  const requestedLocationBase = locationBaseFromPath();

  useEffect(() => {
    fetch('/api/auth/check')
      .then(r => r.json())
      .then(d => setAuthenticated(d.authenticated))
      .catch(() => setAuthenticated(false));
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [b, k] = await Promise.all([fetchBoxen(), fetchKategorien()]);
      setBoxen(b);
      setKategorien(k);
    } catch (err) {
      if (err.message === '401') { setAuthenticated(false); return; }
      setError('Fehler beim Laden: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authenticated) loadData(); }, [authenticated, loadData]);

  useEffect(() => {
    if (!authenticated || loading || directLinkHandled || !requestedBoxNumber) return;
    const pendingLocation = loadPendingLocation();
    if (pendingLocation) {
      setLocationAssignment({
        baseCode: pendingLocation.baseCode,
        level: pendingLocation.level,
        boxNumber: requestedBoxNumber,
      });
      setDirectLinkHandled(true);
      return;
    }
    const requestedBox = boxen.find(
      box => box.box_nummer.toUpperCase() === requestedBoxNumber
    );
    if (requestedBox) {
      setEditBox(requestedBox);
      setNewBoxNumber('');
      setShowForm(true);
    } else {
      setEditBox(null);
      setNewBoxNumber(requestedBoxNumber);
      setShowForm(true);
      setError('');
    }
    setDirectLinkHandled(true);
  }, [authenticated, loading, directLinkHandled, requestedBoxNumber, boxen]);

  useEffect(() => {
    if (!authenticated || loading || directLocationHandled || !requestedLocationBase) return;
    const level = suggestLevel(boxen, requestedLocationBase);
    savePendingLocation(requestedLocationBase, level);
    setLocationAssignment({ baseCode: requestedLocationBase, level, boxNumber: '' });
    setDirectLocationHandled(true);
  }, [authenticated, loading, directLocationHandled, requestedLocationBase, boxen]);

  async function handleLogout() {
    await logout();
    setAuthenticated(false);
    setBoxen([]);
    setKategorien([]);
  }

  if (authenticated === null) return null; // kurz warten während Auth-Check
  if (!authenticated) return <LoginPage onLogin={() => { setLoading(true); setAuthenticated(true); }} />;

  function handleSaved(saved) {
    setBoxen(prev => {
      const exists = prev.find(b => b.id === saved.id);
      const next = exists ? prev.map(b => b.id === saved.id ? saved : b) : [...prev, saved];
      return next.sort((a, b) => a.box_nummer.localeCompare(b.box_nummer, undefined, { numeric: true, sensitivity: 'base' }));
    });
    if (saved.kategorie && !kategorien.includes(saved.kategorie)) {
      setKategorien(prev => [...prev, saved.kategorie].sort());
    }
    // Formular bleibt offen – Nutzer schliesst manuell über "Schliessen"
  }

  function handleDeleted(id) {
    setBoxen(prev => prev.filter(b => b.id !== id));
  }

  function handleEdit(box) {
    setEditBox(box);
    setNewBoxNumber('');
    setShowForm(true);
  }

  function handleNewBox() {
    setEditBox(null);
    setNewBoxNumber('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditBox(null);
    setNewBoxNumber('');
    if (boxNumberFromPath()) window.history.replaceState({}, '', '/');
  }

  function closeLocationAssignment() {
    clearPendingLocation();
    setLocationAssignment(null);
    if (boxNumberFromPath() || locationBaseFromPath()) window.history.replaceState({}, '', '/');
  }

  // Filtern & Suchen
  const preparedBoxes = boxen.filter(isPreparedBox);
  const activeBoxes = boxen.filter(box => !isPreparedBox(box));
  const sourceBoxes = showPrepared || search ? boxen : activeBoxes;
  const filtered = sourceBoxes.filter(b => {
    const katMatch = !filterKat || b.kategorie === filterKat;
    const searchLower = search.toLowerCase();
    const searchMatch = !search ||
      b.box_nummer.toLowerCase().includes(searchLower) ||
      (b.kategorie || '').toLowerCase().includes(searchLower) ||
      (b.inhalt_stichworte || '').toLowerCase().includes(searchLower) ||
      (b.standort || '').toLowerCase().includes(searchLower) ||
      (b.erinnerung_text || '').toLowerCase().includes(searchLower) ||
      reminderDateKey(b.erinnerung_am).includes(searchLower);
    return katMatch && searchMatch;
  });

  // Hierarchische Gruppierung: Hauptboxen + zugehörige Subboxen
  function buildTree(boxes) {
    const hauptboxen = boxes.filter(b => !b.parent_box);
    const subboxen = boxes.filter(b => b.parent_box);

    const result = [];
    for (const h of hauptboxen) {
      result.push({ box: h, isSub: false });
      const kinder = subboxen.filter(s => s.parent_box === h.box_nummer);
      for (const k of kinder) {
        result.push({ box: k, isSub: true });
      }
    }
    // Subboxen ohne bekannte Hauptbox trotzdem anzeigen
    const orphans = subboxen.filter(s => !hauptboxen.find(h => h.box_nummer === s.parent_box));
    for (const o of orphans) {
      result.push({ box: o, isSub: true });
    }
    return result;
  }

  const tree = buildTree(filtered);
  const today = localDateKey();
  const dueReminders = boxen
    .filter(box => {
      const dateKey = reminderDateKey(box.erinnerung_am);
      return dateKey && dateKey <= today;
    })
    .sort((a, b) => reminderDateKey(a.erinnerung_am).localeCompare(reminderDateKey(b.erinnerung_am)));

  return (
    <div className="lager-app">
      <header className="app-header">
        <div className="app-header-inner">
          <div className="app-brand">
            <span className="app-brand-mark" aria-hidden="true">L</span>
            <div>
              <div className="app-brand-kicker">Dachboden</div>
              <h1 className="app-brand-title">Lagerverwaltung</h1>
            </div>
          </div>
          <div className="app-header-actions">
            <button className="app-button app-button-quiet desktop-action" onClick={() => window.open('/api/boxen/export/csv')}>
              Export
            </button>
            <button
              className="app-button app-button-secondary"
              onClick={() => setShowLabelPrint(true)}
              title="A4-Etiketten drucken"
              aria-label="A4-Etiketten drucken"
            >
              <span aria-hidden="true">▦</span><span className="button-label">Etiketten</span>
            </button>
            <button
              className="app-button app-button-secondary"
              onClick={() => setShowLocationPrint(true)}
              title="Dachboden-Stellfelder markieren"
              aria-label="Dachboden-Stellfelder markieren"
            >
              <span aria-hidden="true">⌗</span><span className="button-label">Stellfelder</span>
            </button>
            <button className="app-button app-button-primary" onClick={handleNewBox}>
              <span aria-hidden="true">＋</span><span>Neue Box</span>
            </button>
            <button className="app-icon-button" onClick={handleLogout} title="Abmelden" aria-label="Abmelden">
              ↪
            </button>
          </div>
        </div>
      </header>

      <div className="app-toolbar-wrap">
        <div className="app-toolbar">
          <label className="search-field">
            <span className="search-symbol" aria-hidden="true">⌕</span>
            <input
              className="search-input"
              placeholder="Box, Inhalt oder Standort suchen"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Boxen durchsuchen"
            />
          </label>
          <select className="category-filter" value={filterKat} onChange={e => setFilterKat(e.target.value)} aria-label="Nach Kategorie filtern">
            <option value="">Alle Kategorien</option>
            {kategorien.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          {(filterKat || search) && (
            <button className="reset-button" onClick={() => { setFilterKat(''); setSearch(''); }}>Zurücksetzen</button>
          )}
        </div>
      </div>

      <main className="app-main">
        {loading && <div className="app-state">Boxen werden geladen…</div>}
        {error && <div className="app-error">{error}</div>}

        {!loading && !error && (
          <>
            {dueReminders.length > 0 && (
              <section className="reminder-overview" aria-labelledby="reminder-heading">
                <div className="reminder-overview-heading">
                  <div>
                    <div className="reminder-overview-kicker">Jetzt prüfen</div>
                    <h2 id="reminder-heading">{dueReminders.length} fällige Erinnerung{dueReminders.length !== 1 ? 'en' : ''}</h2>
                  </div>
                  <span className="reminder-overview-mark" aria-hidden="true">!</span>
                </div>
                <div className="reminder-overview-list">
                  {dueReminders.map(box => (
                    <button key={box.id} className="reminder-overview-item" onClick={() => handleEdit(box)}>
                      <span className="reminder-overview-box">{box.box_nummer}</span>
                      <span className="reminder-overview-copy">
                        <strong>{box.erinnerung_text || 'Box prüfen'}</strong>
                        <small>Fällig am {formatReminderDate(box.erinnerung_am)}</small>
                      </span>
                      <span className="reminder-overview-open">Öffnen</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            <div className="overview-heading">
              <div>
                <div className="overview-kicker">Übersicht</div>
                <h2 className="overview-title">Deine Boxen</h2>
              </div>
              <div className="overview-stats" aria-label="Bestandsübersicht">
                <span className="stat-pill"><strong>{activeBoxes.length}</strong> befüllt</span>
                {dueReminders.length > 0 && <span className="stat-pill stat-pill-due"><strong>{dueReminders.length}</strong> fällig</span>}
                {preparedBoxes.length > 0 && <span className="stat-pill stat-pill-muted"><strong>{preparedBoxes.length}</strong> vorbereitet</span>}
                {(filterKat || search || showPrepared) && <span className="stat-pill stat-pill-accent"><strong>{filtered.length}</strong> angezeigt</span>}
              </div>
            </div>
            {preparedBoxes.length > 0 && (
              <button className="prepared-toggle" onClick={() => setShowPrepared(value => !value)}>
                {showPrepared ? 'Vorbereitete Boxen ausblenden' : `${preparedBoxes.length} vorbereitete Box${preparedBoxes.length !== 1 ? 'en' : ''} anzeigen`}
              </button>
            )}
            {tree.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-mark" aria-hidden="true">□</div>
                <h3>Noch keine befüllten Boxen</h3>
                <p>Lege die erste Box an und dokumentiere ihren Inhalt direkt mit Fotos.</p>
                <button className="app-button app-button-primary" onClick={handleNewBox}>
                  Erste Box anlegen
                </button>
              </div>
            ) : (
              <div className="box-grid">
                {tree.map(({ box, isSub }) => (
                  <BoxKarte
                    key={box.id}
                    box={box}
                    isSubBox={isSub}
                    onEdit={handleEdit}
                    onDeleted={handleDeleted}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Formular-Modal */}
      {showForm && (
        <BoxForm
          box={editBox}
          initialBoxNumber={newBoxNumber}
          kategorien={kategorien}
          onSaved={handleSaved}
          onCancel={closeForm}
        />
      )}

      {showLabelPrint && (
        <LabelPrint
          onClose={() => setShowLabelPrint(false)}
        />
      )}

      {showLocationPrint && (
        <LocationPrint onClose={() => setShowLocationPrint(false)} />
      )}

      {locationAssignment && (
        <LocationAssign
          baseCode={locationAssignment.baseCode}
          initialLevel={locationAssignment.level}
          initialBoxNumber={locationAssignment.boxNumber}
          boxes={boxen}
          onAssigned={handleSaved}
          onClose={closeLocationAssignment}
        />
      )}
    </div>
  );
}
